import { z } from 'zod';
export const readingSectionSchema = z
    .object({
    id: z.string().regex(/^[a-z0-9-]+$/),
    title: z.string().trim().min(1).max(120),
    purpose: z.string().trim().min(1).max(400),
})
    .strict();
export const readingDocumentSchema = z
    .object({
    version: z.literal(1),
    kind: z.enum(['lesson', 'workshop']),
    sections: z.array(readingSectionSchema).min(1).max(30),
})
    .strict();
/** Hide authoring navigation syntax in exported briefs and human-readable source. */
export function readingBody(source) {
    const body = source
        .replace(/^```reading-sections\s*\n[\s\S]*?\n```\s*\n/, '')
        .replace(/^<!-- section: [a-z0-9-]+ -->\s*$/gm, '');
    return body === source ? source : body.trim();
}
/** Metadata travels with the Markdown body; no separate database schema is needed. */
export function parseReadingDocument(source) {
    const match = /^```reading-sections\s*\n([\s\S]*?)\n```\s*\n/.exec(source);
    if (!match)
        return null;
    const metadata = readingDocumentSchema.parse(JSON.parse(match[1] ?? ''));
    const body = source.slice(match[0].length);
    const parts = body.split(/^<!-- section: ([a-z0-9-]+) -->\s*$/m);
    const ids = parts.filter((_, index) => index % 2 === 1);
    if (parts[0]?.trim() ||
        ids.length !== metadata.sections.length ||
        new Set(ids).size !== ids.length)
        throw new Error('Reading section boundaries must match their metadata.');
    const sections = metadata.sections.map((section, index) => {
        const text = parts[index * 2 + 2]?.trim();
        if (ids[index] !== section.id || !text)
            throw new Error(`Invalid reading section: ${section.id}`);
        const marker = `<!-- section: ${section.id} -->`;
        const markerStart = source.indexOf(marker, match[0].length);
        const afterMarker = markerStart + marker.length;
        const leading = /^\s*/.exec(source.slice(afterMarker))?.[0].length ?? 0;
        const sourceStartCharacter = afterMarker + leading;
        const sourceStartLine = source.slice(0, sourceStartCharacter).split(/\r\n|\r|\n/).length;
        return { ...section, body: text, sourceStartCharacter, sourceStartLine };
    });
    return { ...metadata, body: readingBody(body), sections };
}
export const studyAidSchema = z
    .object({
    version: z.literal(1),
    kind: z.enum(['map', 'cards']),
    question: z.string().trim().min(1).max(500),
    branches: z
        .array(z
        .object({
        label: z.string().min(1).max(120),
        relation: z.string().min(1).max(200),
        why: z.string().min(1).max(2000),
        apply: z.string().min(1).max(2000),
    })
        .strict())
        .min(1)
        .max(12),
    cards: z
        .array(z
        .object({
        question: z.string().min(1).max(2000),
        answer: z.string().min(1).max(4000),
        transfer: z.string().min(1).max(2000),
    })
        .strict())
        .min(1)
        .max(30),
})
    .strict();
export const collabboardModelSchema = z.enum([
    'representation',
    'execution',
    'persistence',
    'persistence-lifetime',
    'persistence-paths',
    'persistence-representation',
    'persistence-snapshot',
    'persistence-validation',
    'persistence-write',
    'persistence-commit',
    'database-writers',
    'database-query',
    'database-schema',
    'database-connection',
    'database-scope',
    'database-parameters',
    'database-transaction',
    'database-identity',
    'relationship-duplication',
    'relationship-membership',
    'relationship-joins',
    'relationship-assignment',
    'schema-constraints',
    'schema-migration',
    'schema-transaction',
    'schema-delete',
    'script-transforms',
    'types-check',
    'types-narrowing',
    'build-pipeline',
    'react-render',
    'react-keys',
    'react-controlled',
    'react-lift',
    'react-effect',
    'react-stale',
    'react-router',
    'auth-storage',
    'auth-guessing',
    'auth-salt',
    'auth-login',
    'session-flow',
    'cookie-attributes',
    'session-store',
    'auth-client-states',
    'perm-matrix',
    'perm-ownership',
    'perm-client-only',
    'perm-status',
    'query-filter',
    'id-guessing',
    'last-manager-race',
    'keyset-paging',
    'layer-sort',
    'dependency-direction',
    'connection-scope',
    'refactor-steps',
    'test-catch',
    'fixture-rollback',
    'override-flow',
    'debug-trace',
    'model-tokens',
    'model-sampling',
    'model-knowledge',
    'model-context',
    'api-exchange',
    'api-finish-reasons',
    'api-failures',
    'api-cost',
    'prompt-parts',
    'prompt-examples',
    'prompt-history',
    'prompt-injection',
    'output-parse',
    'output-validate',
    'output-schema',
    'output-truncation',
    'feature-flow',
    'feature-dependency',
    'feature-log',
    'feature-decision',
    'eval-match',
    'eval-run',
    'eval-coding',
    'eval-sampling',
    'judge-grade',
    'judge-agreement',
    'judge-drift',
    'judge-cost',
    'experiment-compare',
    'experiment-regression',
    'experiment-settings',
    'experiment-latency',
    'stream-events',
    'stream-relay',
    'stream-failure',
    'stream-choice',
    'ops-cache',
    'ops-budget',
    'ops-backoff',
    'ops-fallback',
    'flow-route',
    'flow-chain',
    'flow-parallel',
    'flow-check',
    'decide-table',
    'decide-guard',
    'decide-shape',
    'decide-test',
    'tool-definition',
    'tool-loop',
    'tool-error',
    'tool-budget',
    'tooldesign-description',
    'tooldesign-consolidate',
    'tooldesign-results',
    'tooldesign-mcp',
    'know-budget',
    'know-chunk',
    'know-keyword',
    'know-miss',
    'embed-space',
    'embed-similarity',
    'embed-hybrid',
    'embed-permission',
    'ground-prompt',
    'ground-cite',
    'ground-abstain',
    'ground-metrics',
    'state-rows',
    'state-resume',
    'state-compact',
    'state-idempotent',
    'approve-rating',
    'approve-pause',
    'approve-resume',
    'approve-audit',
    'inject-flow',
    'inject-trifecta',
    'inject-defense',
    'inject-test',
    'agenteval-trials',
    'agenteval-passk',
    'agenteval-trace',
    'agenteval-table',
    'made-stages',
    'made-preference',
    'made-families',
    'made-benchmark',
    'choose-models',
    'choose-route',
    'choose-thinking',
    'choose-cache',
    'pattern-react',
    'pattern-plan',
    'pattern-reflect',
    'pattern-choice',
    'memory-rows',
    'memory-recall',
    'kb-pipeline',
    'kb-rerank',
    'multi-handoff',
    'multi-cost',
    'multi-guardrail',
    'multi-cache',
    'unattended-run',
    'unattended-limits',
    'unattended-trigger',
    'unattended-coding',
    'networks-ownership',
    'networks-addresses',
    'networks-connection',
    'networks-stream',
    'networks-failures',
    'http-agreement',
    'http-request',
    'http-outcomes',
    'http-framing',
    'http-validation',
    'http-commit',
    'api-responsibilities',
    'api-registration',
    'api-inputs',
    'api-validation',
    'api-resources',
    'api-acceptance',
    'api-locking',
    'web-layers',
    'web-structure',
    'web-layout',
    'web-values',
    'web-rendering',
    'web-events',
    'web-origin',
    'web-await',
    'web-read-states',
    'web-creation',
    'web-actions',
    'web-order',
    'stream',
    'http',
    'framework',
    'browser-layers',
    'browser-network',
]);
